import { type AxiosResponse } from 'axios'
import { type AxiosOptions } from 'shared/api'
import { baseApi } from 'entities/Auth'
import {
  type IFullWorkflowData,
  type IBaseWorkflowData,
  type IParamsEnrollIntoWorkflow,
  type IParamsWorkflowList,
  type IPreviewWorkflowData,
  type IResponseEnrollCheck,
  type IResponseGetWorkflowById,
  type IResponseWorkflowCreate,
  type IResponseWorkflowDelete,
  type IResponseWorkflowDisable,
  type IResponseWorkflowEnable,
  type IResponseWorkflowList,
  type IResponseWorkflowUpdate,
  type IWorkflowExecutionRequest,
  type IResponseWorkflowExecution,
  type IResponseWorkflowTerminateExecution,
  type IResponseUpdateWorkflowsExecutionsList,
  type IParamsUpdateWorkflowsExecutionsList,
} from './types'

class Api {
  getWorkflows<Params extends IParamsWorkflowList = IParamsWorkflowList>(
    params?: Params,
    options?: Omit<AxiosOptions, 'params'>
  ): Promise<
    AxiosResponse<
      IResponseWorkflowList<
        Params['preview'] extends true ? IPreviewWorkflowData : IFullWorkflowData
      >
    >
  > {
    return baseApi.get('workflows/workflows', { ...options, params })
  }

  getWorkflowById(id: string): Promise<AxiosResponse<IResponseGetWorkflowById>> {
    return baseApi.get(`workflows/workflows/${id}`)
  }

  createWorkflow(payload: IBaseWorkflowData): Promise<AxiosResponse<IResponseWorkflowCreate>> {
    return baseApi.post('workflows/workflows', payload)
  }

  updateWorkflow(
    id: string,
    payload: IBaseWorkflowData
  ): Promise<AxiosResponse<IResponseWorkflowUpdate>> {
    return baseApi.post(`workflows/workflows/${id}`, payload)
  }

  enableWorkflow(id: string): Promise<AxiosResponse<IResponseWorkflowEnable>> {
    return baseApi.post(`workflows/workflows/${id}/enable`)
  }

  disableWorkflow(id: string): Promise<AxiosResponse<IResponseWorkflowDisable>> {
    return baseApi.post(`workflows/workflows/${id}/disable`)
  }

  deleteWorkflow(id: string): Promise<AxiosResponse<IResponseWorkflowDelete>> {
    return baseApi.delete(`workflows/workflows/${id}`)
  }

  enrollIntoWorkflow(
    params: IParamsEnrollIntoWorkflow,
    id: string
  ): Promise<AxiosResponse<IResponseEnrollCheck>> {
    return baseApi.post(`workflows/workflows/${id}/enroll`, params)
  }

  getConversationExecutions(
    id: number,
    params: IWorkflowExecutionRequest,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseWorkflowExecution>> {
    return baseApi.get(`workflows/executions/conversation/${id}`, { ...options, params })
  }

  terminateExecution(
    executionId: string
  ): Promise<AxiosResponse<IResponseWorkflowTerminateExecution>> {
    return baseApi.post(`workflows/executions/${executionId}/terminate`)
  }

  updateWorkflowsExecutionsList(
    params: IParamsUpdateWorkflowsExecutionsList,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseUpdateWorkflowsExecutionsList>> {
    return baseApi.post('workflows/executions/list', params, options)
  }
}

export const WorkflowsApi = new Api()
